/**
 * useCommissionCostingEnabled — the RENDER seam for commission tracking and job costing
 * (LDG-1.1, AR-1/AR-4).
 *
 * Single source of truth for "should this account see commission or costing at all?".
 * Inputs: the active account's `commission_costing` module in the account registry, read through
 * `my_account_modules()` (RBAC-6.1, migration 20261102b8; it was the `tenant_settings` column from
 * LDG-1.2's 20260819a5, which is now a mirror). The decision itself is
 * the pure `isCommissionCostingEnabled` in `@/lib/tenantFeatureGates`; this hook only
 * wires the live input to it, which is the split `useDesignFeatureAccess` established.
 *
 * ⚠️ THERE IS NO SUPERADMIN BYPASS, AND THAT IS THE DIFFERENCE FROM
 * `useDesignFeatureAccess`. That gate is a role allowlist, so letting `system.override`
 * through is right: the person who can open the gate can pass it. This one is a per-account
 * ROLLOUT gate. A superadmin looking at an account that was never enabled must see exactly
 * what that account's own users see, because the alternative is reading a half-built money
 * system's numbers for a tenant that has none, and then acting on them. Enabling an account
 * is a deliberate act with an audit row (the Settings → Commission card); it is not
 * something a superadmin should get by accident just by switching accounts.
 *
 * ⚠️ WHILE `loading` IS TRUE, advertising surfaces (sidebar, Settings cards a
 * user did not open) stay absent. A Commission destination the user already
 * clicked may paint a layout-matched skeleton (NAV-5.7). Do not treat
 * `enabled===false` as account-off until `loading` is false.
 *
 * ⚠️ THIS HOOK IS NOT A CONTEXT. Two calls used to each start at
 * `settings=null`. The module cache in `commissionCostingEnabledCache` is the
 * shared answer, keyed by tenant, so DestinationPage and NavViewer cannot
 * disagree.
 *
 * ⚠️ THIS IS NOT THE GATE. It decides what to render and nothing else. The gate is the
 * database function `commission_enabled(tenant_id)` (LDG-1.2), which triggers and the
 * joules-admin-backend workers call, because everything this feature writes is automatic.
 * A check in a browser cannot stop a trigger.
 */
import { useEffect, useState } from 'react';
import { usePermissions } from '@/contexts/PermissionContext';
import { useTenant } from '@/contexts/TenantContext';
import {
  getOrStartCommissionCostingEnabledFetch,
  initialCommissionCostingEnabledState,
} from '@/lib/commissionCostingEnabledCache';
import {
  isCommissionCostingEnabled,
  type TenantGateSettings,
} from '@/lib/tenantFeatureGates';
import { isModuleActive } from '@/lib/accountFeatures';
import { fetchMyAccountModules } from '@/services/accountModulesService';

export interface CommissionCostingAccess {
  enabled: boolean;
  loading: boolean;
  settingsLoading: boolean;
}

export function useCommissionCostingEnabled(): CommissionCostingAccess {
  const { loading: permsLoading } = usePermissions();
  const { currentTenant } = useTenant();

  // W1R P13: the ACTIVE tenant from TenantContext — NOT `userInfo.tenant_id`, which is the
  // superadmin's HOME tenant and does not change on an account switch, so the
  // re-read this effect promises never fired for the one audience that
  // switches accounts. (my_account_modules() resolves the active account
  // server-side either way; this key only decides WHEN to re-fetch.)
  const tenantId = currentTenant?.tenant_id;
  const seeded = initialCommissionCostingEnabledState(tenantId);
  const [settings, setSettings] = useState<TenantGateSettings | null>(seeded.settings);
  const [settingsLoading, setSettingsLoading] = useState(seeded.settingsLoading);

  useEffect(() => {
    let cancelled = false;
    const next = initialCommissionCostingEnabledState(tenantId);
    setSettings(next.settings);
    setSettingsLoading(next.settingsLoading);

    getOrStartCommissionCostingEnabledFetch(tenantId, () =>
      // RBAC-6.1: the account registry (module commission_costing), not the tenant_settings column,
      // which is now only a mirror and reads stale once a trial lapses. Mapped into the shape
      // isCommissionCostingEnabled already decides on, so the decision itself is unchanged.
      fetchMyAccountModules()
        .then((mods): TenantGateSettings => ({
          commission_costing: isModuleActive(mods, 'commission_costing'),
        }))
        .catch(() => {
          // Fail closed. A fetch failure, a missing settings row and a tenant that was never
          // enabled all have to give the same answer, or "absence means off" is a comment
          // rather than a behaviour.
          return null;
        }),
    )
      .then((s) => {
        if (!cancelled) setSettings(s);
      })
      .catch(() => {
        if (!cancelled) setSettings(null);
      })
      .finally(() => {
        if (!cancelled) setSettingsLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // Re-read when the active tenant changes, so a superadmin account switch cannot leave a
    // previously-enabled account's answer on screen.
  }, [tenantId]);

  return {
    enabled: isCommissionCostingEnabled(settings),
    loading: permsLoading || settingsLoading,
    settingsLoading,
  };
}
